import { Tooltip, icons } from '@lifebit-ai/lds'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { DataAccessRequestStatus as DataAccessRequestStatusBadge } from 'modules/requests/data-access-requests/components/DataAccessRequestStatus'
import { ApprovedIcon, PendingIcon, RejectedIcon } from 'modules/requests/filters/StatusFilter.styles'
import { getDataAccessRequestDetailsPath } from 'modules/requests/requestDetailPaths'
import * as styles from 'modules/resultsV2/ResultMeta.styles'
import {
  DatasetChip,
  TEST_ID_DATA_ACCESS_REQUEST_DATASET_COLOR
} from 'modules/resultsV2/components/DatasetChip/DatasetChip'
import {
  PARTICIPANTS_IN_DATASET_LABEL,
  PARTICIPANTS_MATCHING_QUERY_LABEL,
  getDataAccessRequestQueryDisplayRows
} from 'modules/resultsV2/hooks/getDataAccessRequestQueryDisplayRows'
import { getDataAccessRequestSharingMetadata } from 'modules/resultsV2/hooks/getDataAccessRequestSharingMetadata'
import { formatCompactParticipantCount } from 'modules/resultsV2/utils'
import {
  DataAccessRequestReviewSubStatus,
  DataAccessRequestSharingDisplayStatus,
  DataAccessRequestStatus
} from 'services/data-access-requests/enums'
import type {
  DataAccessRequestDetail,
  DataAccessRequestSharing,
  DataAccessRequestVerificationDetail
} from 'services/data-access-requests/types'
import type { DataAccessRequestQuerySource } from 'services/results/types'
import type { StudyId } from 'services/shared/schemas/studyIdSchema'
import { CardLink } from 'ui/card/card.styles'
import { formatDate } from 'utils/date'

export const TEST_ID_DATA_ACCESS_REQUEST_METADATA = 'data-access-request-metadata'
export const TEST_ID_DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_METADATA =
  'data-access-request-additional-information-metadata'

const PLACEHOLDER_VALUE = '-'
export { TEST_ID_DATA_ACCESS_REQUEST_DATASET_COLOR }
export const TEST_ID_DATA_ACCESS_REQUEST_STUDY_ID = 'data-access-request-study-id'
export const TEST_ID_DATA_ACCESS_REQUEST_STATUS = 'data-access-request-status'
export const TEST_ID_DATA_ACCESS_REQUEST_SHARING_STATUS = 'data-access-request-sharing-status'
export const TEST_ID_DATA_ACCESS_REQUEST_TARGET_WORKSPACE = 'data-access-request-target-workspace'
export const TEST_ID_DATA_ACCESS_REQUEST_SHARING_FAILURE_REASON = 'data-access-request-sharing-failure-reason'
export const TEST_ID_DATA_ACCESS_REQUEST_PARTICIPANT_COUNT = 'data-access-request-participant-count'

const SHARING_STATUS_LABEL = 'Sharing status'
const TARGET_WORKSPACE_LABEL = 'Target workspace'
const SHARING_FAILURE_REASON_LABEL = 'Failure reason'
export const TARGET_WORKSPACE_INFO_TEXT = 'Workspace the dataset schema is shared with once the Study ID is matched'

const SHARING_STATUS_ICONS: Record<DataAccessRequestSharingDisplayStatus, JSX.Element> = {
  [DataAccessRequestSharingDisplayStatus.Shared]: <ApprovedIcon aria-hidden />,
  [DataAccessRequestSharingDisplayStatus.Processing]: <PendingIcon aria-hidden />,
  [DataAccessRequestSharingDisplayStatus.Failed]: <RejectedIcon aria-hidden />,
  [DataAccessRequestSharingDisplayStatus.NotShared]: <RejectedIcon aria-hidden />
}

export const ORGANISATION_INFO_TEXT = 'The Organisation that owns the data'
export const FEDERATED_WORKSPACE_INFO_TEXT =
  'Workspace assigned for Federated Connections (Airlock Managers of this workspace will review DAR Requests)'
export const REQUESTING_WORKSPACE_INFO_TEXT =
  'Workspace where a user discovers data and requests access to it via DAR Flow'

export interface DataAccessRequestMetadataSectionProps {
  requestingUserEmail?: string
  requestedAt: string
  requesterOrganisationName?: string
  sourceWorkspaceName?: string
  requesterWorkspaceName?: string
  datasetName: string
  datasetColor: string
  requestStatus: DataAccessRequestStatus
  reviewSubStatus?: DataAccessRequestReviewSubStatus
  studyId?: StudyId
  querySource?: DataAccessRequestQuerySource
  dataAccessRequestDetail?: DataAccessRequestDetail
  sharing?: DataAccessRequestSharing
  metadataTestId?: string
  parentRequest?: DataAccessRequestVerificationDetail
  parentRequestLinkId?: string
  rejectedAt?: string
  rejectedByEmail?: string
  rejectionReason?: string | null
  showRejectionReason?: boolean
  showSharingMetadata?: boolean
}

interface MetadataLabelWithInfoProps {
  label: string
  info: string
}

const MetadataLabelWithInfo = ({ label, info }: MetadataLabelWithInfoProps) => {
  return (
    <styles.MetadataLabel>
      <span>{label}</span>
      <Tooltip title={info}>
        <styles.MetadataInfoTrigger aria-label={info}>
          <styles.MetadataInfoIcon />
        </styles.MetadataInfoTrigger>
      </Tooltip>
    </styles.MetadataLabel>
  )
}

export const DataAccessRequestMetadataSection = ({
  requestingUserEmail,
  requestedAt,
  requesterOrganisationName,
  sourceWorkspaceName,
  requesterWorkspaceName,
  datasetName,
  datasetColor,
  requestStatus,
  reviewSubStatus,
  studyId,
  querySource,
  dataAccessRequestDetail,
  sharing,
  metadataTestId = TEST_ID_DATA_ACCESS_REQUEST_METADATA,
  parentRequest,
  parentRequestLinkId,
  rejectedAt,
  rejectedByEmail,
  rejectionReason,
  showRejectionReason = false,
  showSharingMetadata = true
}: DataAccessRequestMetadataSectionProps): JSX.Element => {
  const { participantCount, isUnrestricted } = querySource
    ? getDataAccessRequestQueryDisplayRows(querySource)
    : { participantCount: undefined, isUnrestricted: false }
  const sharingMetadata = showSharingMetadata
    ? getDataAccessRequestSharingMetadata({
        status: requestStatus,
        studyId,
        dataAccessRequestDetail,
        sharing
      })
    : undefined
  const hasParentRequestLink = parentRequestLinkId !== undefined || parentRequest !== undefined
  const submittedOnLabel = hasParentRequestLink ? 'Submitted on' : 'Requested on'
  const resolvedParentRequestLinkId = parentRequestLinkId ?? parentRequest?._id

  return (
    <styles.ItemRow>
      <styles.MetadataSection data-testid={metadataTestId}>
        <styles.MetadataRow>
          <styles.MetadataLabel>Requested by</styles.MetadataLabel>
          <styles.MetadataValue>{requestingUserEmail}</styles.MetadataValue>
        </styles.MetadataRow>
        <styles.MetadataRow>
          <styles.MetadataLabel>{submittedOnLabel}</styles.MetadataLabel>
          <styles.MetadataValue>{formatDate(requestedAt)}</styles.MetadataValue>
        </styles.MetadataRow>
        {requesterOrganisationName && (
          <styles.MetadataRow>
            <MetadataLabelWithInfo label="Organisation" info={ORGANISATION_INFO_TEXT} />
            <styles.MetadataValue>{requesterOrganisationName}</styles.MetadataValue>
          </styles.MetadataRow>
        )}
        {sourceWorkspaceName && (
          <styles.MetadataRow>
            <MetadataLabelWithInfo label="Federated Workspace" info={FEDERATED_WORKSPACE_INFO_TEXT} />
            <styles.MetadataValue>{sourceWorkspaceName}</styles.MetadataValue>
          </styles.MetadataRow>
        )}
        {requesterWorkspaceName && (
          <styles.MetadataRow>
            <MetadataLabelWithInfo label="Requesting Workspace" info={REQUESTING_WORKSPACE_INFO_TEXT} />
            <styles.MetadataValue>{requesterWorkspaceName}</styles.MetadataValue>
          </styles.MetadataRow>
        )}
        <styles.MetadataRow>
          <styles.MetadataLabel>Dataset</styles.MetadataLabel>
          <styles.MetadataValue>
            <DatasetChip name={datasetName} color={datasetColor} />
          </styles.MetadataValue>
        </styles.MetadataRow>
        <styles.MetadataRow data-testid={TEST_ID_DATA_ACCESS_REQUEST_STUDY_ID}>
          <styles.MetadataLabel>Study ID</styles.MetadataLabel>
          <styles.MetadataValue>{studyId ?? '-'}</styles.MetadataValue>
        </styles.MetadataRow>
        <styles.MetadataRow data-testid={TEST_ID_DATA_ACCESS_REQUEST_STATUS}>
          <styles.MetadataLabel>Status</styles.MetadataLabel>
          <styles.MetadataValue>
            <DataAccessRequestStatusBadge reviewSubStatus={reviewSubStatus} status={requestStatus} />
          </styles.MetadataValue>
        </styles.MetadataRow>
        {showRejectionReason ? (
          <styles.MetadataRow>
            <styles.MetadataLabel>Reason</styles.MetadataLabel>
            <styles.MetadataValue>{rejectionReason ?? PLACEHOLDER_VALUE}</styles.MetadataValue>
          </styles.MetadataRow>
        ) : null}
        {showRejectionReason ? (
          <>
            <styles.MetadataRow>
              <styles.MetadataLabel>Rejected by</styles.MetadataLabel>
              <styles.MetadataValue>{rejectedByEmail ?? PLACEHOLDER_VALUE}</styles.MetadataValue>
            </styles.MetadataRow>
            <styles.MetadataRow>
              <styles.MetadataLabel>Rejected on</styles.MetadataLabel>
              <styles.MetadataValue>
                {rejectedAt !== undefined ? formatDate(rejectedAt) : PLACEHOLDER_VALUE}
              </styles.MetadataValue>
            </styles.MetadataRow>
          </>
        ) : null}
        {sharingMetadata !== undefined ? (
          <styles.MetadataRow data-testid={TEST_ID_DATA_ACCESS_REQUEST_SHARING_STATUS}>
            <styles.MetadataLabel>{SHARING_STATUS_LABEL}</styles.MetadataLabel>
            <styles.MetadataValue>
              <styles.SharingStatusValue>
                {SHARING_STATUS_ICONS[sharingMetadata.sharingDisplayStatus]}
                <span>{sharingMetadata.sharingStatusLabel}</span>
              </styles.SharingStatusValue>
            </styles.MetadataValue>
          </styles.MetadataRow>
        ) : null}
        {sharingMetadata?.targetWorkspaceName !== undefined && (
          <styles.MetadataRow data-testid={TEST_ID_DATA_ACCESS_REQUEST_TARGET_WORKSPACE}>
            <MetadataLabelWithInfo label={TARGET_WORKSPACE_LABEL} info={TARGET_WORKSPACE_INFO_TEXT} />
            <styles.MetadataValue>{sharingMetadata.targetWorkspaceName}</styles.MetadataValue>
          </styles.MetadataRow>
        )}
        {sharingMetadata?.failureReason !== undefined && sharingMetadata.failureReason.length > 0 ? (
          <styles.MetadataRow data-testid={TEST_ID_DATA_ACCESS_REQUEST_SHARING_FAILURE_REASON}>
            <styles.MetadataLabel>{SHARING_FAILURE_REASON_LABEL}</styles.MetadataLabel>
            <styles.MetadataValue>{sharingMetadata.failureReason}</styles.MetadataValue>
          </styles.MetadataRow>
        ) : null}
        {participantCount !== undefined && (
          <styles.MetadataRow data-testid={TEST_ID_DATA_ACCESS_REQUEST_PARTICIPANT_COUNT}>
            <styles.MetadataLabel>
              {isUnrestricted ? PARTICIPANTS_IN_DATASET_LABEL : PARTICIPANTS_MATCHING_QUERY_LABEL}
            </styles.MetadataLabel>
            <styles.MetadataValue>
              <styles.ParticipantCount>
                <icons.UserGroupSmall aria-hidden />
                <span>
                  {formatCompactParticipantCount(participantCount.matched)} of{' '}
                  {formatCompactParticipantCount(participantCount.total)}
                </span>
              </styles.ParticipantCount>
            </styles.MetadataValue>
          </styles.MetadataRow>
        )}
        {resolvedParentRequestLinkId !== undefined ? (
          <styles.MetadataRow>
            <styles.MetadataLabel>Original request link</styles.MetadataLabel>
            <styles.MetadataValue>
              <CardLink as={RouterLink} to={getDataAccessRequestDetailsPath(resolvedParentRequestLinkId)}>
                <span>Original request</span>
                <icons.OpenSmall aria-hidden />
              </CardLink>
            </styles.MetadataValue>
          </styles.MetadataRow>
        ) : null}
      </styles.MetadataSection>
    </styles.ItemRow>
  )
}
